import { Size } from '@ps/web-core-enums';
import { Translation, useScreenSize } from '@ps/web-shared-components';
import React, { useContext, useState } from 'react';
import { useSelector } from 'react-redux';

import useSearchParams from '@swf/core/hooks/useSearchParams';
import { select } from '@swf/core/store';
import { NavigationTabs } from '@swf/poker';

import { PrizePlayerProviderContext } from '../../../../providers/PrizePlayerProvider';
import { PrizeDistributionModalTabs } from '../../../../types';
import { GameType } from '../../../../types/Game';
import { getGameParams } from '../../../../utils';
import type { SearchParams } from '../PrizeDistributionModal';
import SkeletonTable from '../SkeletonTable/SkeletonTable';
import useFetchPrize from '../hooks/useFetchPrize';
import styles from '../shared.scss';

import HoldemMaxMobileContent from './HoldemMaxMobileContent';
import PrizeDistributionContent from './PrizeDistributionContent';
import {
  TXTCLI_Blind_Levels,
  TXTCLI_Information,
  TXTCLI_Prizes,
  TXTCLI_Something_went_wrongX_please_t_ELL
} from './PrizeDistributionModalContent.messages.json';

const TAB_HOLDEM_MAX_TRANSLATIONS = {
  [PrizeDistributionModalTabs.information]: TXTCLI_Information,
  [PrizeDistributionModalTabs.prizes]: TXTCLI_Prizes
};
const TAB_TRANSLATIONS = {
  ...TAB_HOLDEM_MAX_TRANSLATIONS,
  [PrizeDistributionModalTabs.blindLevels]: TXTCLI_Blind_Levels
};

const PrizeDistributionModalContent: React.FC = () => {
  const { gameType, scriptId, buyIn } = useSearchParams<SearchParams>();
  const [loading, prizeData] = useFetchPrize(scriptId);
  const isMobile = useSelector(select.device.isMobile);
  const { greaterThan } = useScreenSize();
  const smallScreen = !greaterThan(Size.Small);
  const [selectedTab, setSelectedTab] = useState<PrizeDistributionModalTabs>(
    PrizeDistributionModalTabs.information
  );
  const isSmallScreen = isMobile || smallScreen;
  const isHoldemMax = gameType === GameType.HoldemMax;
  const isHoldemMaxMobile = isHoldemMax && (isMobile || smallScreen);
  const numUsersArray = prizeData?.modLevels?.map((modLevel) => modLevel.numUsers) || [];
  const weightsArrayList = prizeData?.modLevels?.map((modLevel) => modLevel.weight) || [];
  const { selectedPlayerIndex } = useContext(PrizePlayerProviderContext);

  if (loading) {
    return <SkeletonTable countRows={5} countColumns={4} tableWidth={isHoldemMax ? 100 : 70} />;
  }

  if (!prizeData) {
    return (
      <div data-testid="empty-container" className={styles.emptyContainer}>
        <Translation {...TXTCLI_Something_went_wrongX_please_t_ELL} />
      </div>
    );
  }

  const { timeBank, initialChips, drawLevels, modLevels, currency, scalePM, buyin, rake } =
    getGameParams(prizeData, gameType);
  const bets = modLevels?.[selectedPlayerIndex]?.drawLevels?.[0]?.stakeLevels?.[0] || '-/-';
  const lowBet = parseInt(bets.split('/')[0]);
  const highBet = parseInt(bets.split('/')[1]);

  return (
    <div className={styles.content}>
      {isSmallScreen && (
        <div className={styles.navContainer}>
          <NavigationTabs
            selectedTab={selectedTab}
            onSelect={(tab: PrizeDistributionModalTabs) => setSelectedTab(tab)}
            tabTranslations={isHoldemMax ? TAB_HOLDEM_MAX_TRANSLATIONS : TAB_TRANSLATIONS}
          />
        </div>
      )}
      {isHoldemMaxMobile ? (
        <HoldemMaxMobileContent
          selectedTab={selectedTab}
          playerCount={numUsersArray}
          weights={weightsArrayList}
          buyIn={buyIn}
          timeBank={timeBank}
          initialChips={initialChips}
          gameType={gameType}
          drawLevels={drawLevels}
          modLevels={modLevels}
          currency={currency}
          scalePM={scalePM}
          buyin={buyin}
          rake={rake}
          lowBet={lowBet}
          highBet={highBet}
        />
      ) : (
        <div className={isHoldemMax ? styles.holdemContainer : styles.contentContainer}>
          <PrizeDistributionContent
            selectedTab={selectedTab}
            playerCount={numUsersArray}
            weights={weightsArrayList}
            buyIn={buyIn}
            timeBank={timeBank}
            initialChips={initialChips}
            gameType={gameType}
            drawLevels={drawLevels}
            modLevels={modLevels}
            currency={currency}
            scalePM={scalePM}
            buyin={buyin}
            rake={rake}
            lowBet={lowBet}
            highBet={highBet}
          />
        </div>
      )}
    </div>
  );
};

export default PrizeDistributionModalContent;
